import React, { useState, useEffect } from "react";

/**
 * BackupLoader - High-end, user-friendly, animated database backup progress dialog.
 * 
 * Props:
 *  - isOpen: boolean
 *  - dbCode: string
 *  - bytesLoaded: number
 *  - status: "preparing" | "downloading" | "success" | "error"
 *  - errorMessage: string
 *  - onClose: () => void
 */
export default function BackupLoader({
  isOpen,
  dbCode = "database",
  bytesLoaded = 0,
  status = "preparing",
  errorMessage = "",
  onClose,
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Timer for elapsed seconds
  useEffect(() => {
    let timer = null;
    if (isOpen && status !== "success" && status !== "error") {
      setElapsedSeconds(0);
      timer = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isOpen, status]);

  if (!isOpen) return null;

  // Format bytes to human readable (KB, MB)
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 KB";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Format seconds to mm:ss
  const formatTime = (totalSec) => {
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  // Dynamic phase message based on elapsed time & transferred bytes
  const getPhaseDetails = () => {
    if (status === "error") {
      return {
        title: "Backup Failed",
        subtitle: errorMessage || "An error occurred while generating the database backup.",
        badgeColor: "bg-rose-50 text-rose-700 border-rose-200",
      };
    }

    if (status === "success") {
      return {
        title: "Backup Downloaded Successfully!",
        subtitle: `Saved to your Downloads folder as "${dbCode}_backup.zip"`,
        badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    }

    if (bytesLoaded > 0) {
      return {
        title: "Transferring Backup Archive...",
        subtitle: `Transferred ${formatBytes(bytesLoaded)} so far. Finalizing zip stream...`,
        badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
      };
    }

    if (elapsedSeconds < 3) {
      return {
        title: "Connecting to Database Engine...",
        subtitle: `Initializing secure snapshot for database [${dbCode}]...`,
        badgeColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
      };
    } else if (elapsedSeconds < 8) {
      return {
        title: "Exporting Tables to XML Schema...",
        subtitle: "Extracting ledger, transaction, and master records...",
        badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
      };
    } else {
      return {
        title: "Compressing Archive to ZIP Package...",
        subtitle: "High-level data compression in progress. Almost ready...",
        badgeColor: "bg-sky-50 text-sky-700 border-sky-200",
      };
    }
  };

  const phase = getPhaseDetails();

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/65 backdrop-blur-sm font-Inter select-none p-4 transition-all duration-300">
      <div className="relative w-full max-w-[390px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Top Gradient Accent Bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-400" />

        <div className="px-5 py-4 flex flex-col items-center text-center">
          {/* Animated Graphic / Icon Header */}
          <div className="relative mb-2.5 flex items-center justify-center">
            {status === "success" ? (
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center text-emerald-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : status === "error" ? (
              <div className="w-12 h-12 rounded-full bg-rose-50 border-2 border-rose-200 flex items-center justify-center text-rose-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            ) : (
              <div className="relative flex items-center justify-center">
                {/* Glowing Outer Rings */}
                <div className="absolute w-15 h-15 rounded-full bg-blue-400/20 animate-ping duration-1000" />
                <div className="w-12 h-12 rounded-full bg-blue-50 border-2 border-blue-200 flex items-center justify-center shadow-inner relative z-10">
                  {/* Database Server Icon with animated pulse */}
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                    />
                  </svg>
                </div>
                {/* Micro floating download arrow badge */}
                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center border-2 border-white shadow-sm z-20 animate-bounce">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Title & Phase Subtitle */}
          <h3 className="text-sm font-bold text-slate-800 tracking-tight">
            {phase.title}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5 max-w-sm leading-tight">
            {phase.subtitle}
          </p>

          {/* Animated Progress Bar */}
          <div className="w-full mt-3.5">
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden relative shadow-inner">
              {status === "success" ? (
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500 w-full" />
              ) : status === "error" ? (
                <div className="h-full bg-rose-500 rounded-full w-full" />
              ) : (
                <div className="h-full bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-400 rounded-full w-full relative overflow-hidden">
                  {/* Infinite Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 animate-shimmer" />
                </div>
              )}
            </div>
          </div>

          {/* Metadata Cards Grid */}
          <div className="w-full grid grid-cols-3 gap-2 mt-3 text-left">
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
              <span className="block text-[8.5px] font-bold text-slate-400 uppercase tracking-wider">DATABASE</span>
              <span className="text-[11px] font-bold text-slate-700 truncate block mt-0.5" title={dbCode}>
                {dbCode}
              </span>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
              <span className="block text-[8.5px] font-bold text-slate-400 uppercase tracking-wider">ELAPSED TIME</span>
              <span className="text-[11px] font-bold text-blue-700 font-Inter block mt-0.5">
                {formatTime(elapsedSeconds)}
              </span>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
              <span className="block text-[8.5px] font-bold text-slate-400 uppercase tracking-wider">FORMAT</span>
              <span className="text-[11px] font-bold text-slate-700 block mt-0.5">
                ZIP (XML)
              </span>
            </div>
          </div>

          {/* Safety Reassurance Notice */}
          <div className="mt-2.5 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-50/70 border border-blue-100 text-blue-800 text-[10.5px] font-medium w-full text-left">
            <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="leading-tight">
              Please do not close or refresh this tab while the backup archive is compiling.
            </span>
          </div>

          {/* Action Button for Success / Error */}
          {(status === "success" || status === "error") && (
            <div className="w-full mt-2.5 flex justify-end">
              <button
                onClick={onClose}
                className={`w-full py-1.5 rounded-lg text-xs font-bold text-white shadow-sm transition cursor-pointer ${
                  status === "success" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"
                }`}
              >
                {status === "success" ? "Done" : "Close"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
